/** Pure helpers for the notifications panel: type labels, deep-link targets and
 * relative time. Kept out of the component module so fast-refresh stays happy. */
import type { NotificationType, NotificationView } from '../../api/schema';

export const TYPE_LABEL: Record<NotificationType, string> = {
  review_backlog: 'Review backlog',
  category_suggested: 'New category suggestions',
  processing_error: 'Processing errors',
  cleanup_summary: 'Cleanup summaries',
};

export const NOTIFICATION_TYPES: readonly NotificationType[] = [
  'review_backlog',
  'category_suggested',
  'processing_error',
  'cleanup_summary',
];

/**
 * Links older backends stored on notifications that point at routes the UI never
 * had. Notifications persist, so those rows keep their link — this maps them onto
 * the screen that actually shows the thing.
 */
function legacyTarget(link: string): string {
  if (link === '/taxonomy/suggestions') return '/taxonomy?tab=suggestions';
  if (link === '/admin/dlq') return '/documents?status=failed';
  if (link.startsWith('/settings/mailboxes/')) return '/settings/mailboxes';
  return link;
}

/** The deep-link target for a notification (its own link wins, else by type). */
export function targetFor(n: NotificationView): string {
  if (n.link != null && n.link !== '') return legacyTarget(n.link);
  switch (n.type) {
    case 'review_backlog':
      return '/review';
    case 'category_suggested':
      return '/taxonomy?tab=suggestions';
    case 'processing_error':
      return '/documents?status=failed';
    case 'cleanup_summary':
      return '/settings/retention';
    default:
      return '/dashboard';
  }
}

/** Compact relative time, e.g. `"2h ago"`. */
export function timeAgo(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.round((now - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${String(mins)}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${String(hours)}h ago`;
  const days = Math.round(hours / 24);
  return `${String(days)}d ago`;
}
